/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  createMultifamilyTourRequest thunk
 */

import { getAPIFriendlyFilters } from '@zcode-ui/filters'
import {
  checkLoggedIn,
  getUser,
  onMessageSent,
  selectIsMobile,
} from '@zcode-ui/web-zumper-global-state'
import { IntegratedTourType, getEnumTitle } from '@zcode/enums'
import { getGclidFromLocalStorage, getMsclkidFromLocalStorage } from '@zcode/legacy-local-storage'
import { TOUR_PREFERENCE_TYPES } from '@zcode/mixpanel'
import { type Origin, localStorageSingleton } from '@zcode/nr-utils'
import getMessageOriginString from '@zcode/nr/es/shared/actions/sendMessage/helper/getMessageOriginString'
import saveMessageUser from '@zcode/nr/es/shared/actions/sendMessage/helper/saveMessageUser'
import trackLeadConversion from '@zcode/nr/es/shared/actions/sendMessage/helper/trackLeadConversion'
import trackOptimizelyMessageEvents from '@zcode/nr/es/shared/actions/sendMessage/helper/trackOptimizelyMessageEvents'
import * as Api from '@zcode/nr/es/shared/api'
import {
  type APPLY,
  type MESSAGE,
  TOUR,
  type WAITLIST,
} from '@zcode/nr/es/shared/constants/message'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import { getVisitedExperiments } from '@zcode/optimizely-client'
import type {
  DlcMessagedProperty,
  ExtraThunkArg,
  Listable,
  Profile,
  Property,
  SendMessageArgs,
} from '@zcode/state-type'
import type { Values } from '@zcode/utility-types'
import { format } from 'date-fns'
import type { Dispatch } from 'redux'
import { addErrorButterBar } from '../../../actions/butter.action'
import { getMessageFavoritesPayload } from '../../../actions/helpers/getMessageFavoritesPayload'
import { handleFollowUpModalAndSuccessAnalyticsAndButter } from '../../../actions/sendMessage/handleFollowUpModalAndSuccessAnalyticsAndButter'
import { setRequestedTour } from '../../../actions/sendMessage/requestedTours'
import { maybeAddEctaSuffix } from '../../../actions/sendMessage/setupMessageData'
import { getViewCountObject } from '../../../components/detail/helpers/viewCount/viewCountObject'
import { getHasInstantTourAvailability } from '../../../components/message/MessageForm/TourOptions/getHasInstantTourAvailability'
import {
  CONTACT_PREFERENCES_VALUES,
  CONTACT_PREFERENCE_OPTIONS,
  HAS_ACTIVE_REQUESTS,
} from '../../../components/multifamily/MultifamilyTourModal/constants'
import { getFavorited } from '../../../inspectors/index'
import { logger } from '../../../logger'
import type { GetState } from '../../../modules/types'
import {
  DLC_SAVED_SEARCH_OPT_IN,
  TOUR_BOOKING_LEAD_SENT,
  TOUR_INTENT_LEAD_SENT,
} from '../../../packages/optimizely/constants/events'
import { selectEntity } from '../../detail/modules/entity/selectors/index'
import { selectFilters } from '../../filters/selectors/index'
import { getFormattedOrigin } from '../../mixpanel/helpers'
import { getPageNameFromRouteProps } from '../../mixpanel/helpers/getPageNameFromRouteProps'
import { mixpanelSendMessage } from '../../mixpanel/thunks/mixpanelSendMessage'
import { setProfile } from '../../profile/actions/index'
import { createSearch } from '../../savedSearchesView/thunks/createSearch/createSearch'
import { loadConversationPostLead } from '../../stream/thunks/loadConversationPostLead'
import { doneSubmitting, resetSelectedDatetime, startSubmitting } from '../actions/index'
import { selectSelectedTourType } from '../selectors/index'

interface CreateMultifamilyTourRequestProps {
  analyticChannel: SendMessageArgs['analyticChannel']
  buildingId: number | null | undefined
  dlcEnabled?: boolean
  dlcMessagedProperties?: readonly DlcMessagedProperty[] | undefined
  entity: Listable | Property
  equalCTAIntent: typeof APPLY | typeof MESSAGE | typeof TOUR | typeof WAITLIST | undefined
  formType?: SendMessageArgs['formType']
  formValues: {
    contactPreference?: `${Values<typeof CONTACT_PREFERENCES_VALUES>}`
    email: string | undefined
    floorplanId: number | null | undefined
    message: string
    messageModified?: boolean
    moveInDate: string | null
    name: string | undefined
    phone: string | null | undefined
    saveSearch?: boolean
    tourType: 2 | 1 | 3 | undefined
    unitId?: number
  }
  ignoreEqualCTAOrigin?: boolean
  listingId: number | null | undefined
  noButter?: boolean
  oneTap?: boolean
  origin: Origin
  requestedTimes: readonly string[]
  selectedTimes: readonly string[]
  selectedFloorplanId?: SendMessageArgs['selectedFloorplanId']
  shouldLimitLeads?: boolean
  showTourSuccessButterBar: boolean
}

export const createMultifamilyTourRequest =
  ({
    analyticChannel,
    buildingId,
    dlcEnabled,
    dlcMessagedProperties,
    equalCTAIntent,
    entity: specifiedEntity,
    formType,
    formValues,
    ignoreEqualCTAOrigin = undefined,
    listingId,
    noButter,
    origin: originProp,
    requestedTimes,
    selectedFloorplanId,
    selectedTimes,
    shouldLimitLeads = undefined,
    showTourSuccessButterBar,
    oneTap = false,
  }: CreateMultifamilyTourRequestProps) =>
  async (dispatch: Dispatch, getState: GetState, { optimizely }: ExtraThunkArg) => {
    let timezone: string | undefined
    if (typeof window !== 'undefined' && window.Intl) {
      timezone = window.Intl.DateTimeFormat().resolvedOptions().timeZone
    }
    const state = getState()
    const selectedTourType = selectSelectedTourType(state)

    const {
      contactPreference,
      email,
      floorplanId,
      message,
      messageModified,
      moveInDate,
      name,
      phone,
      saveSearch,
      unitId,
    } = formValues
    // move in date should be provided to make successful api request
    const moveInDateObj = moveInDate ? new Date(moveInDate) : null
    const moveInParam = moveInDateObj ? format(moveInDateObj, 'yyyy/MM/dd') : null
    const origin = ignoreEqualCTAOrigin ? originProp : maybeAddEctaSuffix(originProp || '', 'Tour')
    const filters = selectFilters(state)
    const listablesQuery = getAPIFriendlyFilters({ filters: filters || {} })
    const entity = specifiedEntity || selectEntity(state)
    const favorites = getMessageFavoritesPayload(getFavorited(state))
    const isMobile = selectIsMobile(state)

    // Optimizely visited experiments
    const visitedExperiments = getVisitedExperiments(optimizely)

    const query = {
      building_id: buildingId,
      contact_preference: contactPreference
        ? (Number.parseInt(contactPreference) as Values<typeof CONTACT_PREFERENCES_VALUES>)
        : CONTACT_PREFERENCES_VALUES[CONTACT_PREFERENCE_OPTIONS.ANY],
      detail_views: getViewCountObject(),
      email,
      favorites,
      filters: listablesQuery,
      floorplan_id: floorplanId,
      gclid: getGclidFromLocalStorage(localStorageSingleton),
      msclkid: getMsclkidFromLocalStorage(localStorageSingleton),
      listing_id: listingId,
      message,
      message_modified: messageModified,
      move_in: moveInParam,
      name,
      origin: getMessageOriginString({
        origin,
        isMobile,
        oneTap,
        variationMap: visitedExperiments,
      }),
      phone,
      start_ons: requestedTimes,
      tenant_tz: timezone,
      type: selectedTourType,
      unit_id: unitId ?? undefined,
    }

    dispatch(startSubmitting())

    if (dlcEnabled) {
      void dispatch(Optimizely.track({ eventKey: DLC_SAVED_SEARCH_OPT_IN }))
    }
    try {
      const result = await Api.tourRequests.submit(query)
      void dispatch(
        loadConversationPostLead({
          buildingId: entity.buildingId,
          listingId: entity.listingId,
          messageId: result.messageId,
        })
      )
      const isLoggedIn = checkLoggedIn(state)
      const isMobile = selectIsMobile(state)
      const user = getUser(state)
      const messagedBuildingIds = buildingId ? [buildingId] : undefined
      const messagedListingIds = listingId ? [listingId] : undefined
      const timestamp = Math.floor(new Date().getTime() / 1000)
      const messageData = {
        email,
        message,
        messagedBuildingIds,
        messagedListingIds,
        moveIn: moveInParam,
        name,
        origin,
        phone: phone ?? undefined,
        selectedTimes,
      }
      const floorplan = entity?.floorplans?.find?.((fp) => fp.listingId === floorplanId)
      const config = {
        analyticChannel,
        dlcMessagedProperties,
        entity,
        filters,
        floorplan,
        isLoggedIn,
        isMobile,
        isMulti: false,
        messageData,
        user,
      } as const
      const { isTour: isTourEnabled } = entity || {}
      const hasInstantTourAvailability = await dispatch(getHasInstantTourAvailability(entity))
      if (window?.location?.pathname) {
        const pageName = getPageNameFromRouteProps({
          routeProps: {
            location: { pathname: window.location.pathname, search: window.location.search },
          },
          isMobile,
        })
        const tourEnabledType = hasInstantTourAvailability
          ? TOUR_PREFERENCE_TYPES.KNOCK
          : TOUR_PREFERENCE_TYPES.OPEN_HOUSE
        const tourPreferenceType = isTourEnabled
          ? tourEnabledType
          : TOUR_PREFERENCE_TYPES.AVAILABILITY
        const includedTourPreferenceType = selectedTimes?.length ? tourPreferenceType : undefined
        const selectedTourTypeValue = selectSelectedTourType(state)
        const formattedOrigin = getFormattedOrigin(origin)

        dispatch(
          mixpanelSendMessage({
            pageName,
            entity,
            formattedOrigin,
            origin,
            selectedTourType: getEnumTitle(IntegratedTourType, selectedTourTypeValue),
            tourPreferenceType: includedTourPreferenceType,
          })
        )
      }
      dispatch(resetSelectedDatetime())
      if (equalCTAIntent === TOUR) {
        if (hasInstantTourAvailability) {
          dispatch(Optimizely.track({ eventKey: TOUR_BOOKING_LEAD_SENT }))
        } else {
          dispatch(Optimizely.track({ eventKey: TOUR_INTENT_LEAD_SENT }))
        }
      }
      dispatch(trackOptimizelyMessageEvents({ entity }))
      trackLeadConversion(config)
      dispatch(
        onMessageSent({
          success: true,
          buildingIds: messagedBuildingIds,
          equalCTAIntent,
          listingIds: messagedListingIds,
          timestamp,
        })
      )
      dispatch(
        saveMessageUser({
          config,
          equalCTAIntent,
          messageData,
          moveInText: undefined,
          setProfile: (profile: Profile) => dispatch(setProfile(profile)),
          timestamp,
          user,
        })
      )
      const dlcProps = {
        equalCTAIntent,
        formType,
        selectedFloorplanId,
      }
      dispatch(
        handleFollowUpModalAndSuccessAnalyticsAndButter({
          config,
          filters,
          hasInstantTourAvailability,
          messageData,
          showTourSuccessButterBar,
          shouldLimitLeads,
          ...(dlcEnabled && dlcProps),
        })
      )
      localStorageSingleton.set('one_tap_form_inputs', {
        ...(localStorageSingleton.get('one_tap_form_inputs') || {}),
        email,
        moveIn: moveInDateObj && format(moveInDateObj, 'MM/dd/yyyy'),
        name,
        phone,
        requestedTimes,
        selectedTimes,
        tourType: selectedTourType,
        ...(floorplan && { floorplan }),
      })
      if (messageData.selectedTimes) {
        setRequestedTour(
          entity,
          hasInstantTourAvailability,
          {
            selectedTimes: messageData.selectedTimes,
            selectedTimesISO8601: requestedTimes,
          },
          floorplanId
        )
      }
    } catch (err) {
      let message = 'There was an error submitting your tour request. Please try again later.'
      // @ts-expect-error
      const { data, status } = err
      if (status === 451 && data && data.field === 'phone') {
        message =
          'There was an issue processing your phone number. Please double check your phone number field.'
      } else if (status === 400 && data && data.reason === HAS_ACTIVE_REQUESTS) {
        message = 'You already have active tour requests for this listing.'
      }
      dispatch(addErrorButterBar({ message }))
      logger.debug('Error submitting multifamily tour request', err)
    }

    dispatch(doneSubmitting())

    if (saveSearch) {
      const entity = selectEntity(state)
      const filters = selectFilters(state)
      const isLoggedIn = checkLoggedIn(state)
      const floorplan = entity?.floorplans?.find((fp) => fp.listingId === floorplanId)

      try {
        await dispatch(
          createSearch({
            email,
            entity: floorplan,
            filters,
            floorplan,
            isLoggedIn,
            origin,
            noButter,
            noConvert: true,
            preventLoginPrompt: true,
          })
        )
      } catch (err) {
        if (!noButter) {
          dispatch(
            addErrorButterBar({
              message: 'There was an error saving your search. Please try again later.',
            })
          )
        }
        logger.debug('Error saving search', err)
      }
      localStorageSingleton.set('preventCreateAlertFromMsg', true)
    }
  }
